import React from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import Button from "../../components/Button.tsx";
import { paginateUsers, assignUserRole, getUser } from "../../services/user.service.ts";
import { listAllRoles, roleApiError } from "../../services/role.service.js";
import { notification } from "../../utils/Notifications.tsx";
import { can } from "../../auth/can.ts";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import { useNavigate } from "react-router-dom";

const AssignUserRoles = () => {
  useAppSettings();
  const navigate = useNavigate();
  const session = useSelector((state: RootState) => state.auth.user);
  const [query, setQuery] = React.useState("");
  const [users, setUsers] = React.useState<any[]>([]);
  const [roles, setRoles] = React.useState<any[]>([]);
  const [selectedUser, setSelectedUser] = React.useState<any>(null);
  const [nextRoleId, setNextRoleId] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const allowed = can("users.assign_role", session);

  React.useEffect(() => {
    if (!allowed) {
      navigate("/roles");
    }
  }, [allowed, navigate]);

  React.useEffect(() => {
    listAllRoles()
      .then((response) => setRoles(Array.isArray(response) ? response : []))
      .catch((error) => notification("Error", roleApiError(error), "danger"));
  }, []);

  const search = async () => {
    setLoading(true);
    try {
      const response = await paginateUsers(
        `type=all&param=${encodeURIComponent(query)}&limit=20&offset=0`,
      );
      setUsers(response?.data || []);
    } catch (error) {
      notification("Error", roleApiError(error), "danger");
    } finally {
      setLoading(false);
    }
  };

  const pickUser = async (userId: number) => {
    try {
      const detail = await getUser(userId);
      setSelectedUser(detail);
      setNextRoleId("");
    } catch (error) {
      notification("Error", roleApiError(error), "danger");
    }
  };

  const confirmAndAssign = async () => {
    if (!selectedUser || !nextRoleId) return;
    const nextRole = roles.find((role) => String(role.id) === String(nextRoleId));
    const confirmed = window.confirm(
      `¿Cambiar el rol?\n\nUsuario: ${selectedUser.name} ${selectedUser.lastName || ""}\nRol actual: ${selectedUser.rol?.name || "—"}\nNuevo rol: ${nextRole?.name || nextRoleId}`,
    );
    if (!confirmed) return;
    try {
      await assignUserRole(selectedUser.id, Number(nextRoleId));
      notification("Éxito", "Rol actualizado", "success");
      await pickUser(selectedUser.id);
      setNextRoleId("");
    } catch (error) {
      notification("Error", roleApiError(error), "danger");
    }
  };

  return (
    <Layout loading={loading}>
      <Title
        baseTitle="Roles"
        basePath="/roles"
        activeTitle="Usuarios"
        title="Asignar rol a usuario"
      />
      <div className="card border-0 m-4 p-4">
        <div className="d-flex gap-2 mb-3">
          <input
            className="form-control"
            placeholder="Buscar por nombre o correo"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <button className="btn btn-azul" type="button" onClick={search}>
            Buscar
          </button>
        </div>
        {users.length === 0 && (
          <p className="text-secondary">Busca un usuario para asignar rol.</p>
        )}
        {users.length > 0 && (
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Correo</th>
                <th>Rol actual</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    {user.name} {user.lastName}
                  </td>
                  <td>{user.email}</td>
                  <td>{user.rol?.name}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      type="button"
                      onClick={() => pickUser(user.id)}
                    >
                      Seleccionar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {selectedUser && (
        <div className="card border-0 m-4 p-4">
          <h5>
            {selectedUser.name} {selectedUser.lastName}
          </h5>
          <p>
            Rol actual: <strong>{selectedUser.rol?.name}</strong>{" "}
            <code>{selectedUser.rol?.code}</code>
            {selectedUser.rol?.isActive === false && (
              <span className="badge bg-warning text-dark ms-2">Inactivo</span>
            )}
          </p>
          <label className="form-label">Nuevo rol (activos)</label>
          <select
            className="form-select mb-3"
            value={nextRoleId}
            onChange={(event) => setNextRoleId(event.target.value)}
          >
            <option value="">— Selecciona —</option>
            {roles
              .filter((role) => role.isActive !== false)
              .map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name} ({role.code})
                </option>
              ))}
          </select>
          <div className="col-md-3">
            <Button
              type="button"
              variant="azul"
              title="Confirmar cambio"
              disabled={!nextRoleId}
              onClick={confirmAndAssign}
            />
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AssignUserRoles;
